import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  Chip,
  CircularProgress,
  Link,
} from "@mui/material";
import api from "../../../../components/api/api";
import { useSnackbar } from "notistack";

// Страница управления витриной: сотрудник выбирает пункт проката и решает,
// какие наименования товаров показывать на публичной витрине-магазине для клиентов.
const ShowcasePage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [points, setPoints] = useState([]);
  const [selectedPointId, setSelectedPointId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(new Set());
  const requestVersion = useRef(0);
  const pending = useRef(new Set());

  useEffect(() => {
    let active = true;
    api
      .get("/rental-points/")
      .then((res) => {
        if (!active) return;
        const loadedPoints = Array.isArray(res.data) ? res.data : [];
        setPoints(loadedPoints);
        if (loadedPoints.length > 0) {
          setSelectedPointId(loadedPoints[0].id);
        }
      })
      .catch(() => {
        if (active) enqueueSnackbar("Не удалось загрузить пункты проката", { variant: "error" });
      });
    return () => { active = false; requestVersion.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRows = useCallback(
    async (pointId) => {
      if (!pointId) return;
      const version = ++requestVersion.current;
      setRows([]);
      setLoading(true);
      try {
        const [candidatesRes, itemsRes] = await Promise.all([
          api.get("/showcase/candidates", { params: { rental_point_id: pointId } }),
          api.get("/showcase/items", { params: { rental_point_id: pointId } }),
        ]);
        if (version !== requestVersion.current) return;
        const items = Array.isArray(itemsRes.data) ? itemsRes.data : [];
        const candidates = Array.isArray(candidatesRes.data) ? candidatesRes.data : [];
        const merged = candidates.map((c) => {
          const item = items.find((i) => i.asset_name === c.asset_name);
          return {
            ...c,
            itemId: item ? item.id : null,
            is_active: item ? item.is_active : false,
            show_quantity: item ? item.show_quantity : false,
          };
        });
        setRows(merged);
      } catch (e) {
        if (version === requestVersion.current) enqueueSnackbar("Не удалось загрузить витрину", { variant: "error" });
      } finally {
        if (version === requestVersion.current) setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    loadRows(selectedPointId);
  }, [selectedPointId, loadRows]);

  const handleToggleActive = async (row) => {
    const version = requestVersion.current;
    const key = `${selectedPointId}:${row.asset_name}`;
    if (pending.current.has(key)) return;
    pending.current.add(key);
    setSaving(new Set(pending.current));
    try {
      if (!row.itemId) {
        const res = await api.post("/showcase/items", {
          rental_point_id: Number(selectedPointId),
          asset_name: row.asset_name,
          is_active: true,
          show_quantity: false,
          display_order: 0,
        });
        if (version !== requestVersion.current) return;
        setRows((prev) =>
          prev.map((r) =>
            r.asset_name === row.asset_name ? { ...r, itemId: res.data.id, is_active: true } : r
          )
        );
      } else {
        const newActive = !row.is_active;
        await api.patch(`/showcase/items/${row.itemId}`, { is_active: newActive });
        if (version !== requestVersion.current) return;
        setRows((prev) =>
          prev.map((r) => (r.asset_name === row.asset_name ? { ...r, is_active: newActive } : r))
        );
      }
    } catch (e) {
      if (version === requestVersion.current) enqueueSnackbar("Не удалось обновить витрину", { variant: "error" });
    } finally {
      pending.current.delete(key);
      setSaving(new Set(pending.current));
    }
  };

  const handleToggleQuantity = async (row) => {
    if (!row.itemId) return;
    const version = requestVersion.current;
    const key = `${selectedPointId}:${row.asset_name}`;
    if (pending.current.has(key)) return;
    pending.current.add(key);
    setSaving(new Set(pending.current));
    try {
      const newVal = !row.show_quantity;
      await api.patch(`/showcase/items/${row.itemId}`, { show_quantity: newVal });
      if (version !== requestVersion.current) return;
      setRows((prev) =>
        prev.map((r) => (r.asset_name === row.asset_name ? { ...r, show_quantity: newVal } : r))
      );
    } catch (e) {
      if (version === requestVersion.current) enqueueSnackbar("Не удалось обновить настройку", { variant: "error" });
    } finally {
      pending.current.delete(key);
      setSaving(new Set(pending.current));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Витрина товаров
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
        Выберите, какие наименования товаров показывать клиентам на публичной витрине по каждому
        пункту проката. Наличие считается автоматически по активам этого пункта.
        Нажмите на название товара, чтобы редактировать его описание, характеристики и фотографии.
      </Typography>

      <FormControl sx={{ width: { xs: "100%", sm: 340 }, minWidth: 0, mb: 3 }} size="small">
        <InputLabel>Пункт проката</InputLabel>
        <Select
          value={selectedPointId}
          label="Пункт проката"
          onChange={(e) => setSelectedPointId(e.target.value)}
        >
          {points.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
              {p.address ? ` — ${p.address}` : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box className="erp-table-scroll"><Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Наименование</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell align="center">В наличии</TableCell>
              <TableCell align="center">На витрине</TableCell>
              <TableCell align="center">Показывать кол-во</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.asset_name} hover>
                <TableCell>{row.asset_id ? (
                  <Link href={`/assets/edit/${row.asset_id}`} underline="hover" sx={{ fontWeight: 600 }}
                    title="Редактировать карточку товара">{row.asset_name}</Link>
                ) : row.asset_name}</TableCell>
                <TableCell>{row.category ? <Chip size="small" label={row.category} /> : "—"}</TableCell>
                <TableCell align="center">
                  {row.available_count} / {row.total_count}
                </TableCell>
                <TableCell align="center">
                  <Switch checked={!!row.is_active} disabled={saving.has(`${selectedPointId}:${row.asset_name}`)}
                    inputProps={{ 'aria-label': `Показывать ${row.asset_name}` }} onChange={() => handleToggleActive(row)} />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!row.show_quantity}
                    disabled={!row.itemId || !row.is_active || saving.has(`${selectedPointId}:${row.asset_name}`)}
                    inputProps={{ 'aria-label': `Количество ${row.asset_name}` }}
                    onChange={() => handleToggleQuantity(row)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" sx={{ opacity: 0.6, py: 2 }}>
                    На этом пункте проката пока нет активов.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table></Box>
      )}
    </Box>
  );
};

export default ShowcasePage;
